import { Component } from '@angular/core';
import { IonicPage, NavParams, ViewController, ToastController, Platform } from 'ionic-angular';
import { DateTime } from 'luxon';
import { AppConfig } from '../../../../../app/app.config';
import { DateService } from '../../../../../services/date/date.service';
import { JackingV2Service } from '../../../../../services/jacking-v2/jacking-v2.service';
import { PtLuxonDate } from '../../../../../models/projects/date.models';
import {
  FinaliseJackingV2Request,
  JackingFormConflictResult,
  JackingFormInstanceEditor
} from '../../../../../models/jacking-v2/jacking.models';
import {JackingDateTimeEntryHelper} from '../../helpers/jacking-date-time-entry.helper';

@IonicPage()
@Component({
  selector: 'page-jacking-cancel-modal',
  templateUrl: 'jacking-cancel-modal.component.html'
})
export class JackingCancelModalComponent {
  config = AppConfig.settings;

  // same mask concept as events (dynamic second digit updated by helper)
  mask: any[] = [new RegExp('[0-2]'), new RegExp('\\d'), ':', new RegExp('[0-5]'), new RegExp('\\d')];
  private dtHelper: JackingDateTimeEntryHelper;

  isWeb: boolean;
  loading: boolean;

  formInstanceId: number;
  editor: JackingFormInstanceEditor;

  // required
  reason = '';

  // context from parent
  activitySpansMultipleDays = false;
  currentDate?: PtLuxonDate;
  firstEventTime?: PtLuxonDate;
  latestEventTime?: PtLuxonDate;

  firstEventTimeShow?: string;
  // UI-only fields (same naming as events rows)
  timeToShow = '';
  selectedLocalDateIso = '';

  // stored value
  cancellationPt: PtLuxonDate;
  timeIsValid = false;

  constructor(
    private platform: Platform,
    private navParams: NavParams,
    private viewCtrl: ViewController,
    private toastCtrl: ToastController,
    private dateService: DateService,
    private jackingV2Service: JackingV2Service
  ) {
    this.dtHelper = new JackingDateTimeEntryHelper(this.dateService);
  }

  async ngOnInit() {
    this.isWeb = this.platform.is('core');

    this.formInstanceId = this.navParams.get('jackingFormInstanceId');
    this.editor = this.navParams.get('editor');
    this.currentDate = this.navParams.get('currentDate');
    this.firstEventTime = this.navParams.get('firstEventTime');
    this.latestEventTime = this.navParams.get('latestEventTime');
    this.activitySpansMultipleDays = !!this.navParams.get('activitySpansMultipleDays');

    if (!this.formInstanceId) {
      this.presentToast('Missing form instance id');
      return;
    }

    if (this.firstEventTime?.dateTime) {
      this.firstEventTimeShow = await this.dateService.setFullTimeString(this.firstEventTime.dateTime);
    }
    // Seed UI strings from an initial PtLuxonDate (latest if available)
    const seedPt = this.latestEventTime || this.firstEventTime;
    if (seedPt?.dateTime) {
      const hydrated = this.dtHelper.hydrateFromPt(seedPt as any);
      this.timeToShow = hydrated.timeToShow;
      this.selectedLocalDateIso = hydrated.selectedLocalDateIso;
      this.dtHelper.updateMask(this.mask, this.timeToShow);

      // store local pt so offset picker works immediately
      this.cancellationPt = seedPt;
      this.timeIsValid = true;
    } else {
      this.timeIsValid = false;
    }
  }

  close(cancelled: boolean) {
    this.viewCtrl.dismiss({ cancelled });
  }

  // identical to events: date changed updates selectedLocalDateIso then rebuild
  async onCancelDateChanged(dt: DateTime) {
    this.selectedLocalDateIso = dt?.toISODate?.() || '';
    await this.onCancelTimeAltered();
  }

  // identical to events: time text changed updates mask then rebuild
  async onCancelTimeTextChanged(time: string) {
    this.timeToShow = (time || '').trim();
    this.dtHelper.updateMask(this.mask, this.timeToShow);
    await this.onCancelTimeAltered();
  }

  // identical to events: build local PtLuxonDate from base date + time string
  private async onCancelTimeAltered() {
    const time = (this.timeToShow || '').trim();

    // if cleared
    if (!time) {
      this.cancellationPt = null;
      this.timeIsValid = false;
      return;
    }

    const baseDate = this.getBaseDateForEntry();
    const ptLocal = await this.dtHelper.buildLocalPtFromTime(time, baseDate);

    if (!ptLocal) {
      this.timeIsValid = false;
      return;
    }

    if (!this.isAbortDateTimeAllowed(ptLocal.dateTime)) {
      this.timeIsValid = false;
      return;
    }

    this.cancellationPt = ptLocal;
    this.timeIsValid = true;
  }

  // identical to events: offset picker emits PtLuxonDate
  onCancelOffsetSelected(ptLocal: PtLuxonDate) {
    if (!ptLocal?.dateTime) {
      return;
    }

    this.cancellationPt = ptLocal;
    // keep UI strings in sync
    const hydrated = this.dtHelper.hydrateFromPt(ptLocal as any);
    this.timeToShow = hydrated.timeToShow;
    this.selectedLocalDateIso = hydrated.selectedLocalDateIso;
    this.dtHelper.updateMask(this.mask, this.timeToShow);

    this.timeIsValid = this.isAbortDateTimeAllowed(ptLocal.dateTime);
  }

  // base date logic mirrors events (but simplified for a single value)
  private getBaseDateForEntry(): DateTime {
    const startUtc = this.firstEventTime?.dateTime || DateTime.utc();
    const startOffsetMinutes = 0; // if you have offsets, pass them in like events does

    const selectedIso = this.activitySpansMultipleDays
      ? (this.selectedLocalDateIso as string)
      : null;

    return this.dtHelper.getBaseDateUtcMidnight(
      startUtc,
      startOffsetMinutes,
      this.activitySpansMultipleDays,
      selectedIso
    );
  }

  private isAbortDateTimeAllowed(dateTime: DateTime): boolean {
    if (!dateTime?.isValid) {
      return false;
    }

    if (this.firstEventTime?.dateTime && dateTime < this.firstEventTime.dateTime) {
      return false;
    }

    if (!this.activitySpansMultipleDays) {
      return true;
    }

    const minAllowed = this.getAbortDateMinDateTime();
    return !minAllowed || dateTime >= minAllowed;
  }

  private getAbortDateMinDateTime(): DateTime | null {
    const anchor = this.latestEventTime?.dateTime || this.firstEventTime?.dateTime;
    if (!anchor) {
      return null;
    }

    return anchor.startOf('day');
  }

  get abortDateMin(): DateTime | null {
    const currentDayStart = this.currentDate?.dateTime?.startOf('day') || null;
    if (currentDayStart) {
      return currentDayStart;
    }

    return this.activitySpansMultipleDays
      ? this.getAbortDateMinDateTime()
      : this.firstEventTime?.dateTime || null;
  }

  get abortDateMax(): DateTime | null {
    return null;
  }

  async confirm() {
    if (!this.reason || !this.reason.trim()) {
      this.presentToast('Please enter a cancellation reason');
      return;
    }

    if (!this.timeIsValid || !this.cancellationPt?.dateTime) {
      this.presentToast('Please enter a valid cancellation date/time');
      return;
    }

    const req: FinaliseJackingV2Request = {
      time: this.cancellationPt.dateTime.toUTC().toISO(),
      timeOffset: this.cancellationPt.dateTime.offset / 60,
      reason: this.reason.trim(),
      instanceEditor: this.editor
    };

    this.loading = true;
    try {
      await this.jackingV2Service.cancelFormInstance(this.formInstanceId, req);
      this.viewCtrl.dismiss({ cancelled: true });
    } catch (e) {
      if (e?.status === 409) {
        const conflict = e?.error as JackingFormConflictResult;
        this.viewCtrl.dismiss({cancelled: false, conflict});
        return;
      }
      this.presentToast('An error occurred');
    } finally {
      this.loading = false;
    }
  }

  private presentToast(message: string) {
    this.toastCtrl.create({ message, duration: 2500, position: 'bottom' }).present();
  }
}



// WEBPACK FOOTER //
// ./src/pages/jacking-activities/v2/components/jacking-cancel-modal/jacking-cancel-modal.component.ts